import React from 'react';
import clsx from 'clsx';
import styles from './InfoCard.module.css';

export interface InfoCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

// InfoCard component with sub-components interface
interface InfoCardComponent extends React.FC<InfoCardProps> {
  Header: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Content: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Row: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Label: React.FC<React.HTMLAttributes<HTMLSpanElement>>;
  Value: React.FC<React.HTMLAttributes<HTMLSpanElement>>;
  ValueWithAction: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Title: React.FC<InfoCardTitleProps>;
  Metadata: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  MetadataText: React.FC<React.HTMLAttributes<HTMLSpanElement>>;
}

export interface InfoCardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const InfoCard: React.FC<InfoCardProps> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.infoCard, className)} {...props}>
      {children}
    </div>
  );
};

// InfoCard Header Sub-component
const InfoCardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.infoHeader, className)} {...props}>
      {children}
    </div>
  );
};

// InfoCard Content Sub-component
const InfoCardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.infoContent, className)} {...props}>
      {children}
    </div>
  );
};

// InfoCard Row Sub-component
const InfoCardRow: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.infoRow, className)} {...props}>
      {children}
    </div>
  );
};

// InfoCard Label Sub-component
const InfoCardLabel: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({ className, children, ...props }) => {
  return (
    <span className={clsx(styles.infoLabel, className)} {...props}>
      {children}
    </span>
  );
};

// InfoCard Value Sub-component
const InfoCardValue: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({ className, children, ...props }) => {
  return (
    <span className={clsx(styles.infoValue, className)} {...props}>
      {children}
    </span>
  );
};

// InfoCard ValueWithAction Sub-component
const InfoCardValueWithAction: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.infoValueWithAction, className)} {...props}>
      {children}
    </div>
  );
};

// InfoCard Title Sub-component
const InfoCardTitle: React.FC<InfoCardTitleProps> = ({ icon, className, children, ...props }) => {
  return (
    <h3 className={clsx(styles.infoTitle, className)} {...props}>
      {icon && <span className={styles.infoTitleIcon}>{icon}</span>}
      {children}
    </h3>
  );
};

// InfoCard Metadata Sub-component
const InfoCardMetadata: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  if (!children || (Array.isArray(children) && children.every((child: React.ReactNode) => child == null))) return null;

  return (
    <div className={clsx(styles.infoMetadata, className)} {...props}>
      {children}
    </div>
  );
};

// InfoCard MetadataText Sub-component
const InfoCardMetadataText: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({ className, children, ...props }) => {
  return (
    <span className={clsx(styles.infoMetadataText, className)} {...props}>
      {children}
    </span>
  );
};

// Attach sub-components to InfoCard
(InfoCard as InfoCardComponent).Header = InfoCardHeader;
(InfoCard as InfoCardComponent).Content = InfoCardContent;
(InfoCard as InfoCardComponent).Row = InfoCardRow;
(InfoCard as InfoCardComponent).Label = InfoCardLabel;
(InfoCard as InfoCardComponent).Value = InfoCardValue;
(InfoCard as InfoCardComponent).ValueWithAction = InfoCardValueWithAction;
(InfoCard as InfoCardComponent).Title = InfoCardTitle;
(InfoCard as InfoCardComponent).Metadata = InfoCardMetadata;
(InfoCard as InfoCardComponent).MetadataText = InfoCardMetadataText;

export default InfoCard as InfoCardComponent;
